import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Subtree, useObserver, useSubtree } from "subtree.js/react";
import { pick } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import { ShellState } from "../../shell/shell-model";
import { StoreSettingsController } from "./store-settings-controller";
import { type SettingsTab, StoreSettingsActions, settingsTabOf } from "./store-settings-model";
import { StoreSettingsPage } from "./store-settings-page";

export function StoreSettingsRoute({ deps }: { deps: AppDeps }) {
  const { storeId = "" } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = settingsTabOf(searchParams.get("tab"));
  const shellState = useSubtree(ShellState);
  const observer = useObserver();
  // The role is the caller's role in the shop in the URL, not in the selected shop: an Owner of
  // one shop can be a Manager of another. Watching the shell's selected store re-renders this when
  // the repository's stores change; `null` means the shop is not in the list (yet).
  observer.watch(shellState.selectedStore);
  const role = deps.storeRepository.find(storeId)?.myRole ?? null;
  // Read once: the controller drops them from the URL as soon as it has shown the message.
  const [callback] = useState(() => {
    const provider = searchParams.get("provider");
    const status = searchParams.get("status");
    return provider && status ? { provider, status } : null;
  });

  return (
    <Subtree
      key="store-settings"
      deps={[storeId, role]}
      controller={() =>
        new StoreSettingsController(
          pick(
            deps,
            "client",
            "authHandler",
            "storeRepository",
            "locationsRepository",
            "providersRepository",
            "countriesRepository",
            "shareLinks",
          ),
          { storeId, tab, role, callback },
          {
            setTab: (next) => setSearchParams(next === "general" ? {} : { tab: next }, { replace: true }),
            clearCallbackParams: () =>
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  next.delete("provider");
                  next.delete("status");
                  return next;
                },
                { replace: true },
              ),
            toDashboard: () => navigate("/cards"),
            openBlankTab: () => {
              const tab = window.open("about:blank", "_blank");
              if (!tab) return null;
              // `opener` is severed rather than passing "noopener", which makes Chrome return no handle at all.
              tab.opener = null;
              return {
                navigate: (url) => {
                  tab.location.href = url;
                },
                close: () => tab.close(),
              };
            },
            openExternal: (url) => window.location.assign(url),
          },
        )
      }
    >
      <TabSync tab={tab} />
      <StoreSettingsPage />
    </Subtree>
  );
}

/** The URL is the tab's home; a back-button change has to reach the controller too. */
function TabSync({ tab }: { tab: SettingsTab }) {
  const actions = useSubtree(StoreSettingsActions);
  useEffect(() => actions.selectTab(tab), [actions, tab]);
  return null;
}
